import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { ModalQueryParam } from 'src/app/models/enums';

@Injectable({
  providedIn: 'root',
})
export class ModalUrlService {
  constructor(
    private router: Router,
    private location: Location,
  ) {}

  async consumeOpenState(queryParam: ModalQueryParam): Promise<boolean> {
    return (await this.consumeOpenValue(queryParam)) !== null;
  }

  async consumeOpenValue(queryParam: ModalQueryParam): Promise<string | null> {
    await this.waitForNavigation();
    const value = this.router.parseUrl(this.router.url).queryParamMap.get(queryParam);
    if (value === null) return null;
    await this.router.navigate([], {
      queryParams: { [queryParam]: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    return value;
  }

  markOpen(queryParam: ModalQueryParam, value = '') {
    this.replaceQueryParam(queryParam, value);
  }

  markClosed(queryParam: ModalQueryParam) {
    this.replaceQueryParam(queryParam, null);
  }

  private replaceQueryParam(queryParam: ModalQueryParam, value: string | null) {
    const urlTree = this.router.parseUrl(this.location.path());
    const { [queryParam]: currentValue, ...otherQueryParams } = urlTree.queryParams;
    if ((currentValue ?? null) === value) return;
    urlTree.queryParams = value === null ? otherQueryParams : { ...otherQueryParams, [queryParam]: value };
    const emptyValuePattern = new RegExp(`([?&]${queryParam})=(?=&|#|$)`);
    const url = this.router.serializeUrl(urlTree).replace(emptyValuePattern, '$1');
    this.location.replaceState(url, '', this.location.getState());
  }

  private async waitForNavigation() {
    if (this.router.navigated && !this.router.currentNavigation()) return;
    await firstValueFrom(
      this.router.events.pipe(
        filter(
          (event) =>
            event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError,
        ),
      ),
    );
  }
}
